import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Wrench, Plus, Loader2, X, Undo2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";
import { fmtDate } from "@/utils/datetime";

type Assignment = { id: string; item_id: string; employee_id: string; assigned_at: string; status: string; expected_return_date: string | null; returned_at: string | null; notes: string | null };
type Item = { id: string; name: string; sku: string | null; is_equipment: boolean };
type Employee = { id: string; position: string; user?: { full_name: string | null; email: string } | null };

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    active: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    returned: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
};

export default function Equipment() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canAssign = hasPerm("inventory.assign_equipment");
    const [rows, setRows] = useState<Assignment[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ item_id: "", employee_id: "", expected_return_date: "", notes: "" });
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const [as, it, em] = await Promise.all([
                api.get("/inventory/equipment"),
                api.get("/inventory/items"),
                api.get("/hr/employees").catch(() => ({ data: { items: [] } })),
            ]);
            setRows(as.data.items || []);
            setItems((it.data.items || []).filter((x: Item) => x.is_equipment));
            setEmployees(em.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true); setErr(null);
        try {
            await api.post("/inventory/equipment/assign", {
                item_id: form.item_id, employee_id: form.employee_id,
                expected_return_date: form.expected_return_date || null,
                notes: form.notes.trim() || null,
            });
            setCreating(false);
            setForm({ item_id: "", employee_id: "", expected_return_date: "", notes: "" });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    const doReturn = async (aid: string) => {
        setBusy(aid); setErr(null);
        try {
            await api.post(`/inventory/equipment/${aid}/return`);
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(null); }
    };

    const empName = (id: string) => {
        const e = employees.find((x) => x.id === id);
        return e ? (e.user?.full_name || e.user?.email || e.position) : id.slice(0, 8) + "…";
    };
    const itemName = (id: string) => {
        const i = items.find((x) => x.id === id);
        return i ? i.name : id.slice(0, 8) + "…";
    };

    return (
        <ModuleGate module="inventory">
            <div className="space-y-4" data-testid="inv-equipment-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <Wrench size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("inventory.equipment.title")}</h1>
                    {canAssign && (
                        <button data-testid="inv-eq-new-btn" onClick={() => setCreating(true)}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            <Plus size={16} /> {t("inventory.equipment.assign")}
                        </button>
                    )}
                </header>
                {err && <div data-testid="inv-eq-err" className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                {!loading && !rows.length && <div className="surface-card p-6 rounded-xl text-sm" style={{ color: "var(--text-muted)", background: "var(--card)", border: "1px solid var(--border)" }} data-testid="inv-eq-empty">{t("inventory.equipment.empty")}</div>}
                {!!rows.length && (
                    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("inventory.equipment.item")}</th>
                                    <th className="text-left px-3 py-2">{t("inventory.equipment.employee")}</th>
                                    <th className="text-left px-3 py-2">{t("inventory.equipment.assigned_at")}</th>
                                    <th className="text-left px-3 py-2">{t("inventory.equipment.expected_return")}</th>
                                    <th className="text-left px-3 py-2">{t("common.status")}</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((a) => {
                                    const tone = STATUS_TONE[a.status] || STATUS_TONE.active;
                                    return (
                                        <tr key={a.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`inv-eq-row-${a.id}`}>
                                            <td className="px-3 py-2" style={{ color: "var(--text)" }}>{itemName(a.item_id)}</td>
                                            <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{empName(a.employee_id)}</td>
                                            <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{fmtDate(a.assigned_at)}</td>
                                            <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{a.expected_return_date || "—"}</td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs" style={{ background: tone.bg, color: tone.fg }} data-testid={`inv-eq-status-${a.id}`}>
                                                    <span aria-hidden="true">●</span>
                                                    <span>{t(`inventory.equipment.status.${a.status}`)}</span>
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {a.status === "active" && canAssign && (
                                                    <button onClick={() => doReturn(a.id)} disabled={busy === a.id} data-testid={`inv-eq-return-${a.id}`}
                                                        className="text-xs inline-flex items-center gap-1 rounded-md px-2 py-1" style={{ background: "var(--input)", color: "var(--text)", minHeight: 32 }}>
                                                        {busy === a.id ? <Loader2 className="animate-spin" size={12} /> : <Undo2 size={12} />} {t("inventory.equipment.action.return")}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {creating && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setCreating(false)}>
                        <div className="w-full max-w-lg rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="inv-eq-modal">
                            <div className="flex items-center gap-2 mb-4">
                                <h2 className="text-lg font-semibold flex-1" style={{ color: "var(--text)" }}>{t("inventory.equipment.assign")}</h2>
                                <button aria-label="close" onClick={() => setCreating(false)}><X size={18} style={{ color: "var(--text-muted)" }} /></button>
                            </div>
                            <form onSubmit={submit} className="space-y-3">
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("inventory.equipment.item")} *</span>
                                    <select required data-testid="inv-eq-item" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                        <option value="">—</option>
                                        {items.map((i) => <option key={i.id} value={i.id}>{i.sku ? `${i.sku} · ` : ""}{i.name}</option>)}
                                    </select>
                                </label>
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("inventory.equipment.employee")} *</span>
                                    <select required data-testid="inv-eq-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                        <option value="">—</option>
                                        {employees.map((e) => <option key={e.id} value={e.id}>{e.user?.full_name || e.user?.email || e.position}</option>)}
                                    </select>
                                </label>
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("inventory.equipment.expected_return")}</span>
                                    <input type="date" data-testid="inv-eq-retdate" value={form.expected_return_date} onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                </label>
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("common.notes")}</span>
                                    <textarea data-testid="inv-eq-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }} />
                                </label>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button type="button" onClick={() => setCreating(false)} className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.cancel")}</button>
                                    <button type="submit" disabled={saving} data-testid="inv-eq-save" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                        {saving ? <Loader2 className="animate-spin" size={14} /> : t("common.save")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
